import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import chunk from 'lodash/chunk';
import startCase from 'lodash/startCase';
import Styles from './EnergiaSolarParceiros.css';
import ContainerHome24 from '@/components/atoms/ContainerHome24';
import { ParceirosSolar } from './ParceirosSolar';
import CustomEmblaCarouselHome24 from '../CustomEmblaCarouselHome24';
import RedButton from '@/components/atoms/RedButton';
import { isMobile } from '@/utils/browser';
import { NumericBullets } from '@/components/atoms/NumericBullets';
import SearchableDropdownV2 from '@/components/atoms/SearchableDropdown/SearchableDropdownV2';

function EnergiaSolarParceiros() {
  const parceiros = useMemo(() => {
    return ParceirosSolar.map((p) => ({
      ...p,
      parceiros: p.parceiros.sort((a, b) => a.nameParceiro.localeCompare(b.nameParceiro)),
    }));
  }, []);

  const [width, setWidth] = useState(typeof window === 'undefined' ? 0 : window.window.innerWidth);
  const [value, setValue] = useState();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [renderDeskResults, setRenderDeskResults] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);

  const [pointerUp, setPointerUp] = useState(true);

  const embla = useRef(null);

  if (typeof window === 'undefined') {
    console.log('');
  } else {
    window.addEventListener('resize', function () {
      setWidth(window.window.innerWidth);
    });
  }

  const chunkedResults = useMemo(() => {
    if (!value) return [];
    if (!renderDeskResults && !isMobile()) return [];

    const filtered = parceiros.find((d) => d.state === value)?.parceiros ?? [];
    const formatted = filtered.map((p) => ({
      ...p,
      endereco: startCase(p.endereco.toLowerCase()),
    }));

    return chunk(formatted, 3).filter(Boolean);
  }, [value, renderDeskResults, parceiros]);

  const changeSlide = (v = 0) => {
    if (!embla.current) return;

    embla.current.scrollTo(v);
  };

  const resetEmbla = useCallback(() => {
    setLoadingResults(true);
    setCurrentSlide(0);

    setTimeout(() => {
      setLoadingResults(false);
    }, 200);
  }, []);

  useEffect(() => {
    if (!chunkedResults || chunkedResults.length === 0) return;

    resetEmbla();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chunkedResults]);

  return (
    <>
      <section id="energia-solar-parceiros">
        {typeof window === 'undefined' ? (
          <></>
        ) : (
          <>
            {width < 992 && (
              <img
                src="assets/energia-solar/parceiros/img-parceiros.svg"
                alt=""
                className="img-parceiros"
              />
            )}
          </>
        )}
        <ContainerHome24 className="parceiros-container">
          {typeof window === 'undefined' ? (
            <></>
          ) : (
            <>
              {width >= 992 && (
                <img
                  src="assets/energia-solar/parceiros/img-parceiros-desk.svg"
                  alt=""
                  className="img-parceiros"
                />
              )}
            </>
          )}
          <div>
            <h2 className="energia-solar-parceiros-titulo mobile">
              Procure por parceiros credenciados e solicite seu orçamento
            </h2>
            <h2 className="energia-solar-parceiros-titulo desk">
              Encontre parceiros credenciados e solicite seu orçamento
            </h2>
            <p className="energia-solar-parceiros-texto">
              Selecione abaixo o estado que deseja encontrar parceiros.
            </p>
            <SearchableDropdownV2
              optionsContainerId={'dropdown-select-state'}
              options={parceiros}
              label="state"
              id="stateName"
              name="stateName"
              onValueChanged={(val) => {
                setValue(val);
                setRenderDeskResults(!!val);
              }}
              placeholder="Selecionar estado"
              autoSelect
            />

            <RedButton
              isButton
              className={clsx('desk btn-buscar-parceiros', renderDeskResults && 'hidden')}
              onClick={() => {
                if (!value) return;

                setRenderDeskResults(true);
              }}
            >
              Buscar parceiros
            </RedButton>

            {loadingResults && <span className="carousel-skeleton desk" />}

            {chunkedResults && chunkedResults.length > 0 && !loadingResults && (
              <>
                <p className="energia-solar-disclaimer">
                  As empresas apresentadas a seguir são indicadas para facilitar a sua experiência.
                  <br />
                  No entanto, é importante que você verifique as condições e qualidade dos produtos
                  e serviços contratados diretamente com essas empresas.
                </p>
                <div className="parceiros-results">
                  <CustomEmblaCarouselHome24
                    customEmblaCarouselRef={embla}
                    enableBullets={chunkedResults.length > 1}
                    detectBulletCount
                    enableMobileArrows
                    onPointer={setPointerUp}
                    onChange={(params) => {
                      setCurrentSlide(params.currentSlide);
                      setSlideCount(params.slideCount);
                    }}
                    options={{ duration: 20 }}
                    renderBullets={
                      renderDeskResults ? (
                        <NumericBullets
                          bulletCount={slideCount}
                          currentBullet={currentSlide}
                          onBulletClick={changeSlide}
                          maxVisibleBullets={5}
                          hideBullet={chunkedResults.length <= 1}
                        />
                      ) : (
                        <span className="parceiros-bullet">{currentSlide + 1}</span>
                      )
                    }
                  >
                    {({ className: slideClass }) => (
                      <>
                        {chunkedResults.map((chunk, chunkIndex) => {
                          return (
                            <div
                              key={`chunk-parceiro-${chunkIndex}`}
                              data-slide-index={chunkIndex}
                              className={slideClass}
                            >
                              <div
                                className={clsx(
                                  'parceiro-slide',
                                  chunkIndex > 0 && renderDeskResults && 'fix-line',
                                  !pointerUp &&
                                    !renderDeskResults &&
                                    chunkIndex > 0 &&
                                    'extra-space',
                                )}
                              >
                                {chunk.map((parceiro, index, pArray) => {
                                  return (
                                    <div
                                      key={`${parceiro.nameParceiro}-${index}`}
                                      className={clsx(
                                        'parceiros',
                                        index === pArray.length - 1 && 'mb-0',
                                      )}
                                    >
                                      <b className="parceiros-titulo">{parceiro.nameParceiro}</b>
                                      <p className="parceiros-endereco">CNPJ: {parceiro.cnpj}</p>
                                      <p className="parceiros-endereco">
                                        Endereço: {parceiro.endereco}
                                      </p>
                                      <a
                                        href={parceiro.url}
                                        target="_blank"
                                        className="parceiros-saiba-mais"
                                      >
                                        Saiba mais
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </CustomEmblaCarouselHome24>
                </div>
              </>
            )}
          </div>
        </ContainerHome24>
      </section>

      <style jsx>{Styles}</style>
    </>
  );
}

export default EnergiaSolarParceiros;
